import { formatCurrency } from '@/app/utils/formatCurrency';
import { SCALE, toScaled } from './invoiceSelection';
import type { FundingChoice } from './invoicePanel';
import { ACCOUNT_BALANCE_LABEL, AMOUNT_BASIS_LABELS } from './amountBasis';
import type { AmountBasis } from './amountBasis';

export { ACCOUNT_BALANCE_LABEL };

export interface BalanceGate {
    isLoading: boolean;
    isError: boolean;
    cashPosition: number | undefined;
    selectionGross: number;
    hasSelection: boolean;
}

export type BalanceStatus = Pick<BalanceGate, 'isLoading' | 'isError' | 'cashPosition'>;

export const BALANCE_LOADING_REASON = 'Checking the account balance.';

export const BALANCE_UNAVAILABLE_REASON = 'Unable to check the account balance.';

export const SUB_PENNY_EXCESS = `less than ${formatCurrency(0.01)}`;

export type OutcomeCard =
    | { kind: 'to_pay'; label: string; amount: string }
    | { kind: 'pot_total'; claimed: string; absorbed: string }
    | { kind: 'loading'; reason: string }
    | { kind: 'unavailable'; reason: string };

/** Compared at the selection's own 4dp scale, so a selection that exactly equals the
 *  balance is never rejected by float error. */
export function exceedsBalance(cashPosition: number, selectionGross: number): boolean {
    return toScaled(selectionGross) > toScaled(cashPosition);
}

/** Loading or unreadable blocks Submit; an exceeded balance does not, because
 *  the over-balance warning is where it is resolved. */
export function balanceUnreadableReason({ isLoading, isError, cashPosition }: BalanceStatus): string | null {
    if (isLoading) return BALANCE_LOADING_REASON;
    if (isError || cashPosition === undefined) return BALANCE_UNAVAILABLE_REASON;
    return null;
}

export function isBalanceUnavailable(status: BalanceStatus): boolean {
    return balanceUnreadableReason(status) === BALANCE_UNAVAILABLE_REASON;
}

/** Ten-thousandths by which the selection exceeds the balance; 0 when nothing is
 *  selected, it fits, or the balance cannot be read. A negative balance adds to it. */
export function excessGross({ isLoading, isError, cashPosition, selectionGross, hasSelection }: BalanceGate): number {
    if (!hasSelection || isLoading || isError || cashPosition === undefined) return 0;
    if (!exceedsBalance(cashPosition, selectionGross)) return 0;
    return toScaled(selectionGross) - toScaled(cashPosition);
}

export function excessText(excess: number): string {
    const amount = formatCurrency(excess / SCALE);
    return amount === formatCurrency(0) ? SUB_PENNY_EXCESS : amount;
}

export function availableBalanceText(status: BalanceStatus): string | null {
    if (balanceUnreadableReason(status) !== null) return null;
    return formatCurrency(status.cashPosition ?? 0);
}

const claimsPot = (choice: FundingChoice | null, position: number): boolean =>
    choice === 'pot_total' && toScaled(position) > 0;

export function outcomeCard(
    status: BalanceStatus,
    selectionGross: number,
    hasSelection: boolean,
    choice: FundingChoice | null,
): OutcomeCard | null {
    const reason = balanceUnreadableReason(status);
    if (reason !== null) return { kind: isBalanceUnavailable(status) ? 'unavailable' : 'loading', reason };
    if (!hasSelection) return null;

    const position = status.cashPosition ?? 0;
    if (!exceedsBalance(position, selectionGross)) return null;
    const amount = excessText(toScaled(selectionGross) - toScaled(position));
    if (claimsPot(choice, position)) return { kind: 'pot_total', claimed: formatCurrency(position), absorbed: amount };
    return { kind: 'to_pay', label: 'Top up to pay (inc VAT)', amount };
}

export function selectionAnnouncement(
    lineCount: number,
    selectionNet: number,
    outcome: OutcomeCard | null,
    availableBalance: string | null,
    basis: AmountBasis = 'net',
): string {
    const labels = AMOUNT_BASIS_LABELS[basis];
    const selection =
        lineCount === 0
            ? 'No lines selected.'
            : `${lineCount} line${lineCount === 1 ? '' : 's'} selected, ${formatCurrency(selectionNet)} ${labels.vat}.`;
    if (outcome === null) {
        return availableBalance === null ? selection : `${selection} ${labels.balance} ${availableBalance}.`;
    }
    if (outcome.kind === 'loading' || outcome.kind === 'unavailable') return `${selection} ${outcome.reason}`;
    if (outcome.kind === 'pot_total') {
        return `${selection} Claimed from pot ${outcome.claimed}. Dealer absorbs ${outcome.absorbed}.`;
    }
    return `${selection} ${outcome.label} ${outcome.amount}.`;
}

export function stackedSummaryText(
    lineCount: number,
    selectionGross: number,
    status: BalanceStatus,
    choice: FundingChoice | null,
): string {
    const lines = `${lineCount} line${lineCount === 1 ? '' : 's'}, ${formatCurrency(selectionGross)} inc VAT`;
    const unreadable = balanceUnreadableReason(status);
    if (unreadable !== null) return `${lines}. ${unreadable}`;

    const position = status.cashPosition ?? 0;
    if (exceedsBalance(position, selectionGross)) {
        const excess = excessText(toScaled(selectionGross) - toScaled(position));
        if (claimsPot(choice, position)) {
            return `${lines}. Claimed from pot ${formatCurrency(position)} · Dealer absorbs ${excess}`;
        }
        return `${lines}. ${excess} to top up`;
    }
    return `${lines}. ${ACCOUNT_BALANCE_LABEL} ${formatCurrency(position)}`;
}
